import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { BACKEND_MAIN_ERROR_CODES } from "@src/appV2/api/code";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { InternalLink, isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { addBrowserListener, openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { type QueryObserverResult } from "@tanstack/react-query";

import { type PlacementCandidate } from "../../../../../PlacementCandidate/types";
import {
  type CreatePlacementApplicationParams,
  PLACEMENT_TYPE,
  type PlacementType,
  useCreatePlacementApplication,
  type WorkerApplicationPreferenceDetails,
} from "../../../../api/useCreatePlacementApplication";
import { type PlacementDetail } from "../../../../api/useGetPlacement";
import { type PlacementResponse } from "../../../../types/fetchPlacements.schema";
import { type PlacementDerivedData } from "../usePlacementPrimaryAction";
import { type usePlacementState } from "../usePlacementState";

interface UseApplicationOperationsParams {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  modals: ReturnType<typeof usePlacementState>["modals"];
  applicationState: ReturnType<typeof usePlacementState>["applicationState"];
  derivedData: PlacementDerivedData;
  refetchPlacement: () => Promise<QueryObserverResult<PlacementResponse>>;
}

/**
 * Handles all application-related operations (Apply, external application, collectpreferences).
 */
export function useApplicationOperations(params: UseApplicationOperationsParams) {
  const {
    placementDetails,
    placementCandidate,
    modals,
    applicationState,
    derivedData,
    refetchPlacement,
  } = params;

  const { showErrorToast } = useToast();
  const errorMessages = useCbhFlag(CbhFeatureFlag.HCP_APP_ERROR_MESSAGES, { defaultValue: {} });
  const { mutateAsync: createPlacementApplication, isLoading: isCreatingPlacementApplication } =
    useCreatePlacementApplication();

  const {
    applicationPreferences,
    setApplicationPreferences,
    pendingActionRef,
    applicationManagementType,
    setApplicationManagementType,
  } = applicationState;
  const { isSignOnBonusPaid, incentiveProgram, bookingReorderingCohort } = derivedData;

  const isApplicationPreferencesEnabled =
    placementDetails.allowApplicationPreferenceCollection ?? false;

  function handleApplicationError(error: unknown) {
    if (!isCbhApiAxiosError(error)) {
      showErrorToast("Failed to apply for job.");
      return;
    }

    if (
      error.response.data.errors.some(
        (error) => error.code === BACKEND_MAIN_ERROR_CODES.candidateNotLicensedForWorkerType
      )
    ) {
      showErrorToast(
        <Text variant="body2" color="inherit">
          Application failed since you aren&apos;t licensed to work in the facility&apos;s state.{" "}
          <InternalLink
            color="inherit"
            sx={{ textDecorationColor: "inherit" }}
            to={DeprecatedGlobalAppV1Paths.LICENSE_MANAGER}
          >
            Tap here
          </InternalLink>{" "}
          to update your licenses.
        </Text>
      );
      return;
    }

    const errorResponse = error.response.data.errors.find((error) => error.code);
    if (errorResponse && errorMessages[errorResponse.code]) {
      showErrorToast(errorMessages[errorResponse.code]);
      return;
    }

    showErrorToast("Failed to apply for job.");
  }

  async function handleApply(applyParams: {
    workerInterviewRequest?: {
      dates: Date[];
      notes: string;
    };
    workerVoiceNote?: {
      rootFolder: string;
      fileKey: string;
    };
    placementType?: PlacementType;
    preferences?: WorkerApplicationPreferenceDetails;
    applicationManagementType?: CreatePlacementApplicationParams["applicationManagementType"];
    scheduleInterview?: {
      start: string;
      end: string;
    };
    hiringEventId?: string;
    skipSuccessModal?: boolean;
    pendingInterview?: boolean;
  }) {
    const {
      workerInterviewRequest,
      workerVoiceNote,
      placementType,
      preferences,
      applicationManagementType: managementType,
      scheduleInterview,
      hiringEventId,
      skipSuccessModal = false,
      pendingInterview,
    } = applyParams;

    try {
      const applicationPayload = {
        placementCandidateId: placementCandidate.id,
        placementDetails,
        ...(isDefined(incentiveProgram) && !isSignOnBonusPaid ? { incentiveProgram } : {}),
        ...(isDefined(workerVoiceNote) && {
          workerVoiceNote,
        }),
        ...(isDefined(workerInterviewRequest) && {
          workerInterviewRequest,
        }),
        ...(isDefined(placementType) && {
          placementType,
        }),
        ...(isDefined(preferences ?? applicationPreferences) && {
          workerApplicationPreferenceDetails: preferences ?? applicationPreferences,
        }),
        ...(isDefined(managementType) && { applicationManagementType: managementType }),
        ...(isDefined(scheduleInterview) && { scheduleInterview }),
        ...(isDefined(hiringEventId) && { hiringEventId }),
        ...(isDefined(bookingReorderingCohort) && { bookingReorderingCohort }),
        ...(pendingInterview && { pendingInterview }),
      };

      const response = await createPlacementApplication(applicationPayload);
      await refetchPlacement();

      // Determine if we should show the application success modal
      const isVerifiedPlacement = placementType !== PLACEMENT_TYPE.unverified;
      const isBookingInterview = isDefined(scheduleInterview);
      const shouldShowSuccessModal =
        isVerifiedPlacement && !isBookingInterview && !skipSuccessModal;

      if (shouldShowSuccessModal) {
        modals.placementApplicationSuccess.openModal();
      }

      return response;
    } catch (error) {
      handleApplicationError(error);
      throw error;
    }
  }

  /**
   * Executes an action immediately when application preferences are disabled.
   * Otherwise, stores the action and opens the preferences modal so it can be
   * executed after the user submits the preferences form.
   */
  async function handleFetchApplicationPreferences(
    action: (preferences?: WorkerApplicationPreferenceDetails) => Promise<void> | void
  ) {
    if (isApplicationPreferencesEnabled) {
      pendingActionRef.current = action;
      modals.applicationPreference.openModal();
    } else {
      await action();
    }
  }

  async function openExternalApplication() {
    await handleFetchApplicationPreferences(async (preferences) => {
      // Store preferences for use when applying externally
      setApplicationPreferences(preferences);

      // Close the JSA confirmation modal before opening the browser to prevent
      // it from being visible when the user returns from the external application
      modals.jobSearchAgreementConfirmation.closeModal();

      if (isCapacitorPlatform()) {
        const browserFinishedListener = await addBrowserListener("browserFinished", async () => {
          modals.externalApplication.openModal();
          await browserFinishedListener.remove();
        });
        await openInBrowser({
          url: placementDetails.webApplicationLink ?? "",
          presentationStyle: "fullscreen",
        });
      } else {
        await openInBrowser({
          url: placementDetails.webApplicationLink ?? "",
          presentationStyle: "fullscreen",
        });
        modals.externalApplication.openModal();
      }
    });
  }

  return {
    handleApply,
    handleFetchApplicationPreferences,
    openExternalApplication,
    isCreatingPlacementApplication,
    applicationPreferences,
    setApplicationPreferences,
    applicationManagementType,
    setApplicationManagementType,
    pendingActionRef,
  };
}

export type ApplicationOperationsReturn = ReturnType<typeof useApplicationOperations>;
